import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Users, Plus, X, Trash2, UserMinus, Edit3, UserPlus, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { useConfirm } from '@/components/ConfirmProvider';
import AccountSidebar from '@/components/AccountSidebar';
import HeaderBar from '@/components/HeaderBar';

const API = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
    { value: 'viewer', label: 'Viewer' },
    { value: 'billing', label: 'Billing' },
    { value: 'editor', label: 'Editor' },
    { value: 'admin', label: 'Admin' },
    { value: 'owner', label: 'Owner' }
];

export default function TeamSettingsPage({ token, user, logout }) {
    const navigate = useNavigate();
    const { teamId } = useParams();
    const confirm = useConfirm();

    const [team, setTeam] = useState(null);
    const [loading, setLoading] = useState(true);
    const [teamName, setTeamName] = useState('');
    const [newMemberEmail, setNewMemberEmail] = useState('');
    const [newMemberRole, setNewMemberRole] = useState('viewer');
    const [inviting, setInviting] = useState(false);
    const [updatingName, setUpdatingName] = useState(false);

    useEffect(() => {
        fetchTeam();
    }, [teamId]);

    const fetchTeam = async () => {
        try {
            const response = await axios.get(`${API}/api/teams/${teamId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            // Check if user has permission to access team settings (owner or admin only)
            const userRole = response.data.user_role;
            if (!['owner', 'admin'].includes(userRole)) {
                toast.error('You do not have permission to access team settings');
                navigate('/account');
                return;
            }
            
            setTeam(response.data);
            setTeamName(response.data.name);
        } catch (error) {
            toast.error('Failed to load team');
            navigate('/account');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateName = async () => {
        if (!teamName.trim()) {
            toast.error('Team name cannot be empty');
            return;
        }
        
        setUpdatingName(true);
        try {
            await axios.put(
                `${API}/api/teams/${teamId}?name=${encodeURIComponent(teamName)}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Team name updated');
            setTeam({ ...team, name: teamName });
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update team name');
        } finally {
            setUpdatingName(false);
        }
    };

    const handleInviteMember = async (e) => {
        if (e) e.preventDefault();
        
        if (!newMemberEmail) {
            toast.error('Please enter an email address');
            return;
        }

        setInviting(true);
        try {
            await axios.post(
                `${API}/api/teams/${teamId}/invite`,
                { email: newMemberEmail, role: newMemberRole },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(`Invitation sent to ${newMemberEmail}`);
            setNewMemberEmail('');
            fetchTeam();
        } catch (error) {
            const detail = error.response?.data?.detail;
            
            // Handle structured error response (team member limit exceeded)
            if (detail && typeof detail === 'object') {
                if (detail.error === 'team_member_limit_exceeded') {
                    toast.error(detail.message, {
                        action: {
                            label: 'Upgrade Plan',
                            onClick: () => navigate('/choose-plan')
                        },
                        duration: 6000
                    });
                } else {
                    toast.error(detail.message || 'Failed to send invitation');
                }
            } else {
                toast.error(detail || 'Failed to send invitation');
            }
        } finally {
            setInviting(false);
        }
    };
    
    const handleInviteKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleInviteMember();
        }
    };

    const handleUpdateMemberRole = async (memberId, newRole) => {
        try {
            await axios.put(
                `${API}/api/teams/${teamId}/members/${memberId}?role=${newRole}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Member role updated');
            fetchTeam();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update role');
        }
    };

    const handleRemoveMember = async (memberId, memberEmail) => {
        const ok = await confirm({
            title: 'Remove team member?',
            description: (
                <>
                    <span className="block font-semibold text-gray-700 mb-1 break-all">
                        {memberEmail}
                    </span>
                    will lose access immediately. You can re-invite them later.
                </>
            ),
            confirmLabel: 'Remove',
            icon: UserMinus,
        });
        if (!ok) return;
        try {
            await axios.delete(
                `${API}/api/teams/${teamId}/members/${memberId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Member removed');
            fetchTeam();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to remove member');
        }
    };

    const handleDeleteTeam = async () => {
        const ok = await confirm({
            title: 'Delete this team?',
            description: (
                <>
                    <span className="block font-semibold text-gray-700 mb-1">
                        {team?.name}
                    </span>
                    will be permanently deleted along with all invites. Analytics data and websites are kept.
                </>
            ),
            confirmLabel: 'Delete team',
        });
        if (!ok) return;
        try {
            await axios.delete(
                `${API}/api/teams/${teamId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Team deleted');
            navigate('/account');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete team');
        }
    };

    const canManageTeam = team?.user_role === 'owner' || team?.user_role === 'admin';

    if (loading) {
        return (
            <div className="settings-page flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    return (
        <div className="settings-page">
            {/* Header Bar */}
            <HeaderBar user={user} logout={logout} />

            {/* Settings Header */}
            <div className="settings-header">
                <div className="settings-header-content settings-header-row">
                    <h1 className="settings-title">Team Settings</h1>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate('/teams')}
                        className="back-button"
                    >
                        ← Back to teams
                    </Button>
                </div>
            </div>

            <div className="settings-container">
                <AccountSidebar token={token} />

                {/* Main Content */}
                <div className="settings-main">
                    <div className="settings-content">
                        {/* Team Hero Header */}
                        <div className="subscription-hero">
                            <div className="subscription-hero__icon">
                                <Users className="w-5 h-5" />
                            </div>
                            <div className="subscription-hero__text">
                                <h2 className="subscription-hero__title">{team?.name}</h2>
                                <p className="subscription-hero__subtitle">
                                    {team?.members?.length || 0} member{(team?.members?.length || 0) !== 1 ? 's' : ''} · Manage team details, invites, and roles.
                                </p>
                            </div>
                        </div>

                        {/* Team Name Section */}
                        {canManageTeam && (
                            <div className="pref-section">
                                <div className="pref-section__header">
                                    <div className="pref-section__icon">
                                        <Edit3 className="w-4 h-4" />
                                    </div>
                                    <div className="pref-section__heading">
                                        <h3 className="pref-section__title">Team name</h3>
                                        <p className="pref-section__subtitle">This name appears in the team switcher and on member invitations.</p>
                                    </div>
                                </div>

                                <form onSubmit={(e) => { e.preventDefault(); handleUpdateName(); }} className="pref-form">
                                    <div className="form-group">
                                        <label className="form-label">Name</label>
                                        <input
                                            type="text"
                                            value={teamName}
                                            onChange={(e) => setTeamName(e.target.value)}
                                            className="form-input"
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        disabled={updatingName || teamName === team?.name}
                                        className="pref-submit-btn"
                                    >
                                        {updatingName ? 'Saving...' : 'Save'}
                                    </button>
                                </form>
                            </div>
                        )}

                        {/* Team Members Section */}
                        <div className="pref-section">
                            <div className="pref-section__header">
                                <div className="pref-section__icon pref-section__icon--violet">
                                    <UserPlus className="w-4 h-4" />
                                </div>
                                <div className="pref-section__heading">
                                    <h3 className="pref-section__title">Members</h3>
                                    <p className="pref-section__subtitle">Invite teammates and manage what each one can do.</p>
                                </div>
                            </div>

                            {/* Invite Member Form */}
                            {canManageTeam && (
                                <form onSubmit={handleInviteMember} className="team-invite-form">
                                    <Input
                                        type="email"
                                        value={newMemberEmail}
                                        onChange={(e) => setNewMemberEmail(e.target.value)}
                                        onKeyPress={handleInviteKeyPress}
                                        placeholder="teammate@example.com"
                                        className="team-invite-form__input"
                                    />
                                    <select
                                        value={newMemberRole}
                                        onChange={(e) => setNewMemberRole(e.target.value)}
                                        className="team-invite-form__select"
                                    >
                                        {ROLES.filter(r => r.value !== 'owner').map(role => (
                                            <option key={role.value} value={role.value}>
                                                {role.label}
                                            </option>
                                        ))}
                                    </select>
                                    <button type="submit" disabled={inviting} className="pref-submit-btn">
                                        {inviting ? 'Inviting...' : 'Send invite'}
                                    </button>
                                </form>
                            )}

                            {/* Members List */}
                            <ul className="team-members-list">
                                {team?.members?.map((member) => (
                                    <li key={member.id} className="team-member-row">
                                        <div className={`team-member-row__avatar team-member-row__avatar--${member.status}`}>
                                            {(member.name || member.email).charAt(0).toUpperCase()}
                                        </div>
                                        <div className="team-member-row__body">
                                            <div className="team-member-row__name">
                                                {member.name || member.email}
                                            </div>
                                            <div className="team-member-row__email">
                                                {member.email}
                                            </div>
                                        </div>
                                        <div className="team-member-row__actions">
                                            {member.status === 'pending' && (
                                                <span className="team-status-pill team-status-pill--pending">Pending</span>
                                            )}
                                            {member.status === 'rejected' && (
                                                <span className="team-status-pill team-status-pill--rejected">Declined</span>
                                            )}
                                            {member.status === 'active' && member.role !== 'owner' && (
                                                <span className="team-status-pill team-status-pill--active">Accepted</span>
                                            )}

                                            {member.role === 'owner' ? (
                                                <span className={`teams-row__role teams-row__role--owner`}>
                                                    Owner
                                                </span>
                                            ) : canManageTeam ? (
                                                <>
                                                    <select
                                                        value={member.role}
                                                        onChange={(e) => handleUpdateMemberRole(member.id, e.target.value)}
                                                        className="team-role-select"
                                                    >
                                                        {ROLES.filter(r => r.value !== 'owner').map(role => (
                                                            <option key={role.value} value={role.value}>
                                                                {role.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <button
                                                        onClick={() => handleRemoveMember(member.id, member.email)}
                                                        className="team-remove-btn"
                                                        aria-label="Remove member"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                </>
                                            ) : (
                                                <span className={`teams-row__role teams-row__role--${member.role}`}>
                                                    {ROLES.find(r => r.value === member.role)?.label || member.role}
                                                </span>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Danger Zone */}
                        {team?.user_role === 'owner' && (
                            <div className="pref-section">
                                <div className="pref-section__header">
                                    <div className="pref-section__icon pref-section__icon--danger">
                                        <AlertTriangle className="w-4 h-4" />
                                    </div>
                                    <div className="pref-section__heading">
                                        <h3 className="pref-section__title">Danger zone</h3>
                                        <p className="pref-section__subtitle">Deleting the team removes all members and invites. Analytics data and websites are kept.</p>
                                    </div>
                                </div>

                                <button
                                    onClick={handleDeleteTeam}
                                    className="danger-trigger-btn"
                                >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete team
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
